/**
 * Development Helper
 * Provides quick authentication for testing (DEV MODE ONLY)
 */

import { supabase } from "@/integrations/supabase/client";

const DEV_TEST_EMAIL = "test@cashee.dev";
const DEV_TEST_PASSWORD = "DevTest123!@#";

// Check for local dev OR Lovable preview environment OR explicit test mode
// NOTE: `?test=true` is intentionally allowed in production for remote prototype testing.
const isTestModeParam = (() => {
  try {
    return new URLSearchParams(window.location.search).get('test') === 'true';
  } catch {
    return false;
  }
})();

// IMPORTANT: Keep this strict.
// - In local dev, allow helper on localhost for speed.
// - In preview or production, only allow when explicitly enabled via `?test=true`.
const isLocalhost = (() => {
  try {
    return window.location.hostname.includes('localhost');
  } catch {
    return false;
  }
})();

export const isDevelopment = isTestModeParam || (import.meta.env.DEV && isLocalhost);

/**
 * Quick login with test account (development only)
 * Creates account if it doesn't exist, then signs in
 */
export const quickDevLogin = async () => {
  if (!isDevelopment) {
    console.warn("Dev helper only available in development mode");
    return { error: new Error("Not in development mode") };
  }

  try {
    // Try to sign in first
    const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
      email: DEV_TEST_EMAIL,
      password: DEV_TEST_PASSWORD,
    });

    if (!signInError && signInData.user) {
      console.log("✅ Dev login successful:", DEV_TEST_EMAIL);
      return { data: signInData, error: null };
    }

    // If sign in failed, try to create account
    if (signInError?.message?.includes("Invalid login credentials")) {
      console.log("📝 Creating dev test account...");
      
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: DEV_TEST_EMAIL,
        password: DEV_TEST_PASSWORD,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (signUpError) throw signUpError;

      console.log("✅ Dev account created and logged in:", DEV_TEST_EMAIL);
      return { data: signUpData, error: null };
    }

    throw signInError;
  } catch (error: any) {
    console.error("❌ Dev login failed:", error);
    return { error };
  }
};

/**
 * Get dev test account credentials (for display only)
 */
export const getDevCredentials = () => {
  if (!isDevelopment) return null;
  
  return {
    email: DEV_TEST_EMAIL,
    password: DEV_TEST_PASSWORD,
  };
};

/**
 * Test badge IDs for testing badge unlock modal
 */
export const getTestBadgeIds = (count: 1 | 2 | 3 = 1): string[] => {
  const testBadges = ['first_expense', 'streak_3_days', 'coffee_lover'];
  return testBadges.slice(0, count);
};
